import Close from '@mui/icons-material/Close';
import { Divider, Popover } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCurrentTenant, UserInfo } from '@qpr/client-library';
import React, { MouseEvent, useEffect, useState } from 'react';

const PREFIX = 'UserModal';

const classes = {
  paper: `${PREFIX}-paper`,
  title: `${PREFIX}-title`,
  value: `${PREFIX}-value`
};

const marginTop = 70;
const Root = styled('div')(({ theme }) => ({
  [`&.${classes.paper}`]: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '5px',
    padding: theme.spacing(2, 2, 3),
    outline: 'none',
    marginTop: `${marginTop}px`
  },

  [`& .${classes.title}`]: {
    paddingLeft: theme.spacing(1),
    color: theme.palette.primary.main
  },

  [`& .${classes.value}`]: {
    marginBottom: '10px',
    minHeight: '20px',
    overflowWrap: 'break-word',
    cursor: 'default'
  }
}));

export type UserModalProps = {
  /**  User information */
  userInfo: UserInfo;
  handleModalClose: () => void;
};

export default function UserModal({ userInfo, handleModalClose }: UserModalProps): JSX.Element {
  const tenant = useCurrentTenant().symbol;
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight - marginTop,
    width: window.innerWidth
  });
  useEffect(() => {
    function handleResize() {
      setDimensions({
        height: window.innerHeight - marginTop,
        width: window.innerWidth
      });
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [anchorPopover, setAnchorPopover] = useState<null | HTMLElement>(null);

  return (
    <Root
      className={classes.paper}
      sx={{ width: dimensions.width, minHeight: dimensions.height }}
      data-testid="user-modal-root"
    >
      <IconButton
        aria-label="close"
        sx={{ float: 'right' }}
        onClick={handleModalClose}
        size="large"
        data-testid="user-modal-close"
      >
        <Close />
      </IconButton>
      <Box sx={{ width: '360px', marginRight: 'auto', marginLeft: 'auto' }}>
        <Typography variant="h6">{userInfo.fullname}</Typography>
        <Typography variant="body2" sx={{ paddingBottom: '10px' }}>
          {userInfo.email}
        </Typography>
        <Divider sx={{ marginBottom: '10px' }} />
        <Typography className={classes.title} variant="caption">
          Full name
        </Typography>
        <Typography className={classes.value} variant="body2">
          {userInfo.fullname}
        </Typography>
        <Typography className={classes.title} variant="caption">
          Login name
        </Typography>
        <Typography className={classes.value} variant="body2">
          {userInfo.username}
        </Typography>
        <Typography className={classes.title} variant="caption">
          Email
        </Typography>
        <Typography className={classes.value} variant="body2">
          {userInfo.email}
        </Typography>
        <Typography className={classes.title} variant="caption">
          Home Page
        </Typography>
        <Typography className={classes.value} variant="body2">
          <span
            onMouseEnter={(event: MouseEvent<HTMLElement>) =>
              setAnchorPopover(userInfo.landingUrl ? event.currentTarget : null)
            }
            onMouseLeave={() => setAnchorPopover(null)}
          >
            {userInfo.landingTitle || userInfo.landingUrl}
          </span>
          <Popover
            sx={{
              pointerEvents: 'none',
              marginTop: '10px',
              boxShadow: 'none',
              MozBoxShadow: 'none',
              WebkitBoxShadow: 'none'
            }}
            open={Boolean(anchorPopover)}
            anchorEl={anchorPopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 0
            }}
          >
            <Typography
              sx={{
                paddingTop: '5px',
                paddingBottom: '5px',
                paddingLeft: '15px',
                paddingRight: '15px',
                background: 'rgb(96, 107, 123)',
                color: 'white',
                fontFamily: 'OpenSans-Regular',
                fontSize: '12px',
                fontWeight: 'normal',
                letterSpacing: '0.4px',
                lineHeight: '16px',
                overflowWrap: 'anywhere',
                textAlign: 'center',
                width: '360px',
                boxShadow: 'none',
                MozBoxShadow: 'none',
                WebkitBoxShadow: 'none'
              }}
            >
              {`${window.location.protocol}//${window.location.hostname}${
                window.location.port ? ':' + window.location.port : ''
              }/${tenant}${userInfo.landingUrl?.startsWith('/') ? '' : '/'}${userInfo.landingUrl}`}
            </Typography>
          </Popover>
        </Typography>
      </Box>
    </Root>
  );
}
